"use client";

import { cn } from "@/lib";
import CurrentScoreIcon from "public/svg/scorePointer.svg";
import Image from "next/image";

interface CreditScoreBarProps {
  currentScore: string;
  className?: string;
  isPdfMode?: boolean;
}

const toPersianDigits = (value: string | number) =>
  String(value).replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);

const CreditHorizontalScoreBar = ({
  currentScore,
  className,
  isPdfMode,
}: CreditScoreBarProps) => {
  // Define all credit score levels in order from worst to best
  const creditLevels = [
    { code: "E3", color: "bg-[#E64747]" },
    { code: "E2", color: "bg-[#FF5656]" },
    { code: "E1", color: "bg-[#FF8A3D]" },
    { code: "D3", color: "bg-[#FBAA19]" },
    { code: "D2", color: "bg-[#FEE114]" },
    { code: "D1", color: "bg-[#D9D92D]" },
    { code: "C3", color: "bg-[#D1D80F]" },
    { code: "C2", color: "bg-[#B5C51B]" },
    { code: "C1", color: "bg-[#9CC829]" },
    { code: "B3", color: "bg-[#84BD32]" },
    { code: "B2", color: "bg-[#6AA52C]" },
    { code: "B1", color: "bg-[#59B53C]" },
    { code: "A3", color: "bg-[#30AD43]" },
    { code: "A2", color: "bg-[#28A23D]" },
    { code: "A1", color: "bg-[#21a85a]" },
  ];

  return (
    <div className={cn("flex flex-col items-center pt-6", className)}>
      {/* Credit Score Bar */}
      <div className="dir-ltr relative mb-12 flex">
        {/* First element with left rounded corner */}
        {creditLevels.map((level, index) => {
          const isCurrentScore = level.code === currentScore;
          const isFirst = index === 0;
          const isLast = index === creditLevels.length - 1;

          return (
            <div
              key={level.code}
              className={cn(
                "text-en relative flex h-[70px] w-[60px] items-center justify-center px-3 py-2 text-xl font-bold text-white! transition-all duration-300",
                isPdfMode && "h-[50px] w-[50px] text-base",
                level.color,
                isFirst && "rounded-l-lg",
                isLast && "rounded-r-lg",
              )}
            >
              {toPersianDigits(level.code)}

              {/* Active Score Indicator */}
              {isCurrentScore && (
                <div
                  className={cn(
                    "absolute -top-13 left-1/2 flex h-[60px] w-[60px] -translate-x-1/2 transform flex-col items-center",
                    isPdfMode && "-top-10 h-[50px] w-[50px]",
                  )}
                >
                  <Image
                    src={CurrentScoreIcon}
                    alt="Current Score Icon"
                    width={100}
                    height={100}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CreditHorizontalScoreBar;
